import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Badge, Box } from '@mui/material';
import { CashbackSet } from 'src/utils';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { view, ViewModel } from '@yoskutik/react-vvm';
import { CardsService, CashbackService } from 'src/services';
import { injectable } from 'tsyringe';
import { FC } from 'react';
import { makeObservable, observable } from 'mobx';
import ListItemIcon from '@mui/material/ListItemIcon';
import CheckIcon from '@mui/icons-material/Check';

export type CashbackSelectorProps = {
  label: string;
  config: CashbackSet;
  onSave: (id: number) => void;
};

type CardConfig = {
  id: number;
  label: string;
  bindings: number;
  isSelected: boolean;
};

@injectable()
class CashbackSelectorViewModel extends ViewModel<
  unknown,
  CashbackSelectorProps
> {
  @observable private readonly selected = new Set<number>();

  constructor(
    private cardService: CardsService,
    private cashbackService: CashbackService,
  ) {
    super();
    makeObservable(this);
  }

  get cards(): CardConfig[] {
    return this.cardService.cards.map((card) => ({
      ...card,
      bindings: this.viewProps.config.items.filter(
        (item) => item.card === card.id,
      ).length,
      isSelected: this.selected.has(card.id),
    }));
  }

  onCardClick(id: number) {
    if (this.selected.has(id)) {
      this.selected.delete(id);
    } else {
      this.selected.add(id);
    }
  }

  onSaveClick = () => {
    this.viewProps.onSave(
      this.cashbackService.add(
        this.viewProps.label,
        this.viewProps.config.items.filter(({ card }) =>
          this.selected.has(card),
        ),
      ),
    );
  };
}

export const CashbackSelector: FC<CashbackSelectorProps> = view(
  CashbackSelectorViewModel,
)(({ viewModel }) => {
  return (
    <>
      <AppBar sx={{ position: 'relative' }} color="default" elevation={0}>
        <Toolbar>
          <Typography sx={{ ml: 2, mr: 'auto' }} variant="h6" component="div">
            Select cashback to copy
          </Typography>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Save"
            onClick={viewModel.onSaveClick}
          >
            <CheckIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ overflow: 'auto' }}>
        <List component="nav">
          {viewModel.cards.map((card) => (
            <ListItemButton
              key={card.id}
              onClick={() => viewModel.onCardClick(card.id)}
            >
              <ListItemIcon>{card.isSelected && <CheckIcon />}</ListItemIcon>
              <ListItemText primary={card.label} />
              {Boolean(card.bindings) && (
                <Badge
                  color="info"
                  badgeContent={card.bindings}
                  sx={{ mr: 1 }}
                />
              )}
            </ListItemButton>
          ))}
        </List>
      </Box>
    </>
  );
});
